import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/outline';

import { lastMonthKst, monthYmdRange } from '../../utils/coupon-settlement.utils';
import { getKstDateParts } from '../../utils/rebate-settlement.utils';

// ─────────────────────────────────────────────
// DateFilter
//
// 미정산 내역의 기간 필터(시작~종료 + 월별 선택 그리드 + 이번달/지난달/초기화).
// 업체정산·상조정산·쿠폰정산 미정산 섹션이 공유한다.
// - 날짜는 한국식 yyyy/mm/dd로 표시하고, 실제 선택은 숨긴 네이티브 달력으로 받는다.
// - "월별 선택"은 연도(◀ ▶)를 오가며 달을 고르면 그 달 1일~말일이 바로 들어간다.
// - onRange를 주면 월/단축 선택 시 onFrom/onTo 대신 한 번에 호출된다
//   (쿠폰정산처럼 선택 즉시 서버 재조회가 필요한 화면용).
// (bugo의 DateFilterSearcher와는 무관한 별개 컴포넌트)
// ─────────────────────────────────────────────

export interface DateFilterProps {
  dateFrom: string;
  dateTo: string;
  onFrom: (v: string) => void;
  onTo: (v: string) => void;
  /** 좌측 라벨 (기본: 기간(배송완료일)) */
  label?: string;
  /** 범위 일괄 변경 콜백 — 월별 선택·이번달·지난달·초기화가 이걸 호출 (미지정 시 onFrom→onTo) */
  onRange?: (from: string, to: string) => void;
  /** 우측 끝 추가 액션 (조회 버튼 등) */
  children?: ReactNode;
}

/** `YYYY-MM-DD` → 한국식 `YYYY/MM/DD` 표시 문자열 */
export const ymdToKr = (ymd: string): string => ymd.replace(/-/g, '/');

const chipClass = (active: boolean) =>
  `rounded-full border px-3 py-1 text-sm transition-colors ${
    active
      ? 'border-indigo-600 bg-indigo-600 text-white'
      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
  }`;

/**
 * 날짜 한 칸 — 화면엔 yyyy/mm/dd 텍스트 버튼만 보이고, 클릭하면 숨겨둔
 * 네이티브 date input의 달력(showPicker)을 연다. 브라우저 로케일(mm/dd/yyyy)
 * 표기를 우회하기 위한 구성.
 */
const DateField: FC<{
  value: string;
  min?: string;
  max?: string;
  placeholder: string;
  onChange: (v: string) => void;
}> = ({ value, min, max, placeholder, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = () => {
    const el = inputRef.current as
      | (HTMLInputElement & { showPicker?: () => void })
      | null;
    if (!el) return;
    try {
      if (typeof el.showPicker === 'function') el.showPicker();
      else el.focus();
    } catch {
      el.focus();
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openPicker}
        className="px-3 py-1.5 text-sm hover:bg-gray-50"
      >
        <span
          className={
            value ? 'font-medium tabular-nums text-gray-800' : 'text-gray-400'
          }
        >
          {value ? ymdToKr(value) : placeholder}
        </span>
      </button>
      <input
        ref={inputRef}
        type="date"
        tabIndex={-1}
        aria-hidden
        value={value}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.target.value)}
        className="absolute bottom-0 left-0 h-px w-px opacity-0"
      />
    </div>
  );
};

const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

export const DateFilter: FC<DateFilterProps> = ({
  dateFrom,
  dateTo,
  onFrom,
  onTo,
  label = '기간(배송완료일)',
  onRange,
  children,
}) => {
  const today = getKstDateParts();
  const lastMonth = lastMonthKst();

  const setRange = (from: string, to: string) => {
    if (onRange) onRange(from, to);
    else {
      onFrom(from);
      onTo(to);
    }
  };

  // 현재 from~to가 정확히 어느 한 달(1일~말일)이면 그 (연,월) — 칩/그리드 하이라이트용
  const selectedMonth = useMemo(() => {
    if (!dateFrom || !dateTo) return null;
    const y = Number(dateFrom.slice(0, 4));
    const m = Number(dateFrom.slice(5, 7));
    if (!y || !m) return null;
    const r = monthYmdRange(y, m);
    return r.from === dateFrom && r.to === dateTo ? { year: y, month: m } : null;
  }, [dateFrom, dateTo]);

  const isMonthSel = (y: number, m: number) =>
    selectedMonth?.year === y && selectedMonth?.month === m;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(today.year);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const close = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [pickerOpen]);

  const openPicker = () => {
    setPickerYear(selectedMonth?.year ?? today.year);
    setPickerOpen((o) => !o);
  };

  const applyMonth = (year: number, month: number) => {
    const r = monthYmdRange(year, month);
    setRange(r.from, r.to);
    setPickerOpen(false);
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 shadow-sm">
      <span className="flex items-center gap-1.5 text-sm font-medium text-gray-600">
        <CalendarIcon className="h-4 w-4 text-gray-400" />
        {label}
      </span>

      {/* 시작일 ~ 종료일 */}
      <div className="flex items-center rounded-lg border border-gray-300">
        <DateField
          value={dateFrom}
          max={dateTo || undefined}
          placeholder="시작일"
          onChange={onFrom}
        />
        <span className="select-none text-gray-300">~</span>
        <DateField
          value={dateTo}
          min={dateFrom || undefined}
          placeholder="종료일"
          onChange={onTo}
        />
      </div>

      {/* 월별 선택 (연도 이동 + 12개월 그리드) */}
      <div ref={pickerRef} className="relative">
        <button
          type="button"
          onClick={openPicker}
          className={`flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-sm transition-colors ${
            selectedMonth
              ? 'border-indigo-300 bg-indigo-50 font-medium text-indigo-700'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {selectedMonth
            ? `${selectedMonth.year}년 ${selectedMonth.month}월`
            : '월별 선택'}
          <ChevronDownIcon
            className={`h-3.5 w-3.5 ${
              selectedMonth ? 'text-indigo-400' : 'text-gray-400'
            }`}
          />
        </button>

        {pickerOpen && (
          <div className="absolute left-0 top-full z-20 mt-1.5 w-60 rounded-xl border border-gray-200 bg-white p-3 shadow-lg">
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setPickerYear((y) => y - 1)}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                aria-label="이전 연도"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-gray-800">
                {pickerYear}년
              </span>
              <button
                type="button"
                disabled={pickerYear >= today.year}
                onClick={() => setPickerYear((y) => y + 1)}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="다음 연도"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1">
              {MONTHS.map((m) => {
                const isFuture =
                  pickerYear === today.year && m > today.month;
                const active = isMonthSel(pickerYear, m);
                return (
                  <button
                    key={m}
                    type="button"
                    disabled={isFuture}
                    onClick={() => applyMonth(pickerYear, m)}
                    className={`rounded-md py-1.5 text-xs transition-colors ${
                      active
                        ? 'bg-indigo-600 font-semibold text-white'
                        : isFuture
                        ? 'cursor-not-allowed text-gray-300'
                        : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-700'
                    }`}
                  >
                    {m}월
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 빠른 선택 */}
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => applyMonth(today.year, today.month)}
          className={chipClass(isMonthSel(today.year, today.month))}
        >
          이번달
        </button>
        <button
          type="button"
          onClick={() => applyMonth(lastMonth.year, lastMonth.month)}
          className={chipClass(isMonthSel(lastMonth.year, lastMonth.month))}
        >
          지난달
        </button>
        {(dateFrom || dateTo) && (
          <button
            type="button"
            onClick={() => setRange('', '')}
            className="rounded-full border border-gray-300 bg-white px-3 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-50"
          >
            전체기간 ✕
          </button>
        )}
      </div>

      {children && (
        <div className="ml-auto flex items-center gap-2">{children}</div>
      )}
    </div>
  );
};
